import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { useSelector } from 'react-redux';

import Button from 'dpl/common/design_system/Button';
import { breederProfileBreederTestimonialIndexResourceDefinition } from 'dpl/shared/breeder/utils/queryDefinitions';
import useInfiniteCollectionQuery from 'dpl/shared/fetching/hooks/useInfiniteCollectionQuery';
import { MOBILE_TAB_KEYS } from 'dpl/components/BreederProfile/utils/constants';
import useBreederProfileTabs from 'dpl/components/BreederProfile/hooks/useBreederProfileTabs';
import OwnerStory from 'dpl/components/OwnerStory';

import BreederProfileReviewsSkeleton from '../../skeletons/BreederProfileReviewsSkeleton';

export default function BreederProfileReviews({
  className,
  navigateToTab,
  reviewsPerPage
}) {
  const { breederFirstName, breederProfileId, testimonialsCount } = useSelector(
    ({ server }) => server
  );
  const { replaceHash } = useBreederProfileTabs();

  const {
    infiniteScrollItems: stories,
    hasNextPage,
    fetchNextPage,
    isLoading,
    isFetching
  } = useInfiniteCollectionQuery(
    breederProfileBreederTestimonialIndexResourceDefinition(breederProfileId, {
      pageNumber: 1,
      perPage: reviewsPerPage
    })
  );

  if (isLoading) {
    return (
      <BreederProfileReviewsSkeleton
        className={className}
        reviewsPerPage={reviewsPerPage}
      />
    );
  }

  if (!isLoading && stories.length === 0) {
    return null;
  }

  return (
    <section
      className={classnames(
        'BreederProfileReviews container-xxl overflow-hidden',
        className
      )}
    >
      <div className="grid gap-8 mb10">
        <h3 className="font-24 lh-title">{breederFirstName}&apos;s reviews</h3>
        <p className="font-16 secondary">
          Hear what other owners say about working with {breederFirstName} to
          bring home their puppies.
        </p>
      </div>

      <ul>
        {stories.map((story, index) => (
          <li key={index}>
            <OwnerStory
              key={story.id}
              id={story.id}
              className={classnames({
                'pb6 pb12-md': index !== stories.length - 1,
                'pt6 pt12-md bt b--default': index
              })}
              date={story.formatted_submitted_at}
              description={story.experience}
              gallery={story.photographs}
              user={story.buyer_profile}
            />
          </li>
        ))}
      </ul>
      {hasNextPage && !navigateToTab && (
        <Button
          className="font-16 stone-700 ph16 pv3 w-100 w-auto-m mt6 mt12-md"
          variant="outline"
          onClick={fetchNextPage}
          disabled={isFetching}
        >
          Show more
        </Button>
      )}
      {hasNextPage && navigateToTab && (
        <Button
          className="font-16 stone-700 ph16 pv3 w-100 w-auto-m mt6 mt12-md"
          variant="outline"
          onClick={() => {
            replaceHash(MOBILE_TAB_KEYS.STORIES);
            window.scrollTo(0, 0);
          }}
          disabled={isFetching}
        >
          Show all {testimonialsCount} reviews
        </Button>
      )}
    </section>
  );
}

BreederProfileReviews.propTypes = {
  className: PropTypes.string,
  navigateToTab: PropTypes.bool,
  reviewsPerPage: PropTypes.number
};

BreederProfileReviews.defaultProps = {
  className: 'mb12 mb16-md',
  navigateToTab: false,
  reviewsPerPage: 10
};
